import { BellOutlined } from "@ant-design/icons";
import { Drawer } from "antd";
import cn from "classnames";
import { lazy, Suspense, useEffect, useState } from "react";

import { useDevice } from "@app/shared/providers/DeviceProvider";
import { useDrawer, useLoc, useSdk } from "@app/shared/lib/hooks";
import { ShellIconButton } from "@app/shared/ui/AppShell/ShellIconButton";
import { LoadingOverlay } from "@app/shared/ui/LoadingOverlay";
import { useUIAppState } from "@app/shared/ui/UIAppState/UIAppStateProvider";

import { UserNotificationFilter } from "@axl/membership-sdk";

import styles from "./Notifications.module.scss";

const UserNotifications = lazy(
  () => import("./components/UserNotifications/UserNotifications"),
);

export const Notifications: React.FC = () => {
  const sdk = useSdk();
  const loc = useLoc();
  const notificationDrawer = useDrawer();
  const [unreadCount, setUnreadCount] = useState(0);
  const device = useDevice();
  const ui = useUIAppState();
  const isMobile = device.deviceSize === "mobile";

  useEffect(() => {
    (async () => {
      const result = await sdk.api.notification.fetchCount(new UserNotificationFilter({ read: false }));
      if (result.success) setUnreadCount(result.body.count);
    })();
  }, [sdk.api.notification]);

  useEffect(() => {
    const unsub = sdk.hub.user.notification.count.subscribe((x) =>
      setUnreadCount(x),
    );

    return () => unsub();
  }, [sdk.hub.user.notification.count]);

  return (
    <>
      <ShellIconButton
        label={loc.word("School.nav.notifications", { default: "Notifications" })}
        icon={<BellOutlined />}
        count={unreadCount}
        onClick={() => notificationDrawer.open()}
      />
      <Drawer
        open={notificationDrawer.visible}
        onClose={() => notificationDrawer.close()}
        closeIcon={null}
        title={null}
        size={isMobile ? "100%" : 420}
        destroyOnHidden
        // the panel floats off the window edges on wide screens; a phone gets the whole screen
        rootClassName={cn(styles.drawer, !isMobile && styles.floating)}
        className="p-0 m-0 no-header"
      >
        <Suspense fallback={<LoadingOverlay />}>
          <UserNotifications
            unreadCount={unreadCount}
            onSettings={() => {
              ui.setIsProfileDrawerVisible(true, "notifications");
              notificationDrawer.close();
            }}
            onClose={() => notificationDrawer.close()}
          />
        </Suspense>
      </Drawer>
    </>
  );
};
